import { useState, useEffect, useRef } from "react";
import { FaFolder, FaFileAlt } from "react-icons/fa";
import ContentViewer from "./ContentViewer";
import { SearchStoreFiles, GetStoreFileListByFolderID, GetStoreFileContentByFileID } from "../utilities/calls";
import CSVTemplateEditor from "./CSVTemplateEditor";

export default function FileManager({
  providerType,
  providerId,
  storeId,
  inline = false
}) {
  const [view, setView] = useState("browser");

  /*
  browser           -> file manager
  filePreview       -> template file preview
  templateEditor    -> CSV editor
  generatedPreview  -> generated PDF preview
  */
  const [activeFile, setActiveFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [folderStack, setFolderStack] = useState([]);
  const folderCache = useRef({});

  const loadFolder = async (folderId) => {
    try {
      if (folderCache.current[folderId]) {
        setFiles(folderCache.current[folderId]);
        return;
      }

      setLoading(true);
      let providerJRN = "jrn:";
      providerJRN += (providerType + ":" + providerId);


      const response = await SearchStoreFiles(
        folderId,
        storeId,
        providerJRN
      );

      const fileList = Array.isArray(response?.items)
        ? response.items.map(item => ({
          ...item,
          isFolder: item.mimeType === "application/vnd.google-apps.folder"
        })) : [];
      folderCache.current[folderId] = fileList;
      setFiles(fileList);

    } catch (err) {
      console.error("Error loading folder:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderById = async (folderId) => {
    try {
      setLoading(true);

      let providerJRN = "jrn:";
      providerJRN += (providerType + ":" + providerId);

      const response = await GetStoreFileListByFolderID(
        folderId,
        storeId,
        providerJRN
      );

      const normalized = (response?.items || []).map(item => ({
        ...item,
        isFolder:
          item.mimeType === "application/vnd.google-apps.folder"
      }));

      setFiles(normalized);
    } catch (err) {
      console.error("Error loading folder:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!storeId || !providerId) return;

    loadFolder("*");
  }, [storeId, providerId]);

  const handleFolderClick = (folder) => {
    setFolderStack((prev) => [...prev, folder]);
    loadFolderById(folder.id);
  };
  const openTemplateEditor = () => {
    setView("templateEditor");
  };
  const handleFilePreview = (item) => {
    try {
      setPreviewLoading(true);
      setPreviewError(null); // clear previous error
      setActiveFile(item);

      let providerJRN = "jrn:" + providerType + ":" + providerId;
      setView("filePreview");

      GetStoreFileContentByFileID(
        item.id,
        storeId,
        providerJRN
      ).then((response) => {
        const blob = new Blob([response.data], {
          type: "application/octet-stream"
        });

        const url = URL.createObjectURL(blob);

        setPreviewUrl(url);
      }).catch(err => {
        console.log(err)

        let message = "Failed to load file preview.";

        if (err?.response?.data?.message) {
          message = err.response.data.message;
        } else if (err?.message) {
          message = err.message;
        }
        setPreviewError(message);
      })
    } catch (err) {
      let message = "Failed to load file preview.";
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.message) {
        message = err.message;
      }
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };
  const handleBreadcrumbClick = (index) => {
    const newStack = folderStack.slice(0, index + 1);
    const folder = newStack[newStack.length - 1];

    setFolderStack(newStack);
    loadFolderById(folder.id);
  };

  const goToRoot = () => {
    setFolderStack([]);
    loadFolder("*");
  };
  const handleClosePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (generatedUrl) URL.revokeObjectURL(generatedUrl);

    setPreviewUrl(null);
    setGeneratedUrl(null);
    setActiveFile(null);
    setView("browser");
    setPreviewError(null);
  };
  
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (generatedUrl) URL.revokeObjectURL(generatedUrl);
    };
  }, [previewUrl, generatedUrl]);

  return (
    <div className={inline ? "" : "p-1"}>
      {/* Breadcrumb */}
      {view === "browser" && (
  <>
    {/* Breadcrumb */}
    <div className="mb-4 text-sm text-gray-400 flex flex-wrap gap-2">
      <span
        onClick={goToRoot}
        className="cursor-pointer hover:text-white"
      >
        Home
      </span>

      {folderStack.map((folder, idx) => (
        <span key={folder.id} className="flex gap-2">
          /
          <span
            onClick={() => handleBreadcrumbClick(idx)}
            className="cursor-pointer hover:text-white"
          >
            {folder.name}
          </span>
        </span>
      ))}
    </div>

    {loading && (
      <div className="text-gray-400 mb-4">Loading...</div>
    )}

    {/* File List */}
    <div className=" h-[80vh] border border-gray-700 rounded-md overflow-hidden overflow-y-auto">
      {files.length === 0 && !loading && (
        <div className="text-gray-400 p-2">No files found.</div>
      )}

      {files.map((item, idx) => (
        <div
          key={item?.id || idx}
          className="flex items-center justify-between px-2 py-2 border-b border-gray-800 hover:bg-gray-800 cursor-pointer"
          onClick={() =>
            item.isFolder
              ? handleFolderClick(item)
              : handleFilePreview(item)
          }
        >
          <div className="flex items-center gap-2 text-white">
            {item.isFolder ? (
              <FaFolder className="text-yellow-400" />
            ) : (
              <FaFileAlt className="text-gray-400" />
            )}
            <span>{item.name}</span>
          </div>

          {!item.isFolder && (
            <span className="text-sm text-blue-400">View</span>
          )}
        </div>
      ))}
    </div>
  </>
)}
      {/* FILE PREVIEW */}
      {view === "filePreview" && (
        <div className="flex flex-col ">

          {/* Preview Header */}
          <div className="flex items-center justify-between mb-1 border-b border-gray-700 pb-1">

            {/* File name */}
            <div className="text-white font-medium truncate">
              {activeFile?.name}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">

              {previewUrl && activeFile?.mimeType === "application/pdf" && (
                <button
                  onClick={openTemplateEditor}
                    className="flex items-center gap-2 px-1 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
                ><FaFileAlt />
                  New PDF from this Template
                </button>
              )}

              <button
                onClick={handleClosePreview}
                className="text-gray-400 hover:text-white text-sm"
              >
                Close
              </button>

            </div>

          </div>
          {previewLoading && (
            <div className="text-gray-400">Loading preview...</div>
          )}
          {previewError && (
            <div className="bg-red-900/30 border border-red-700 text-red-300 p-3 rounded mb-4">
              {previewError}
            </div>
          )}
    {/* Viewer */}
    {previewUrl && <ContentViewer
      url={previewUrl}
      filename={activeFile?.name}
    />}

  </div>
)}

      {/* TEMPLATE EDITOR */}
      {view === "templateEditor" && (
        <CSVTemplateEditor
          onCancel={() => { setView("filePreview"); window.scrollTo(0, 0);}}
          onClosePreview={handleClosePreview}
          fileId={activeFile.id}
          storeId={storeId}
          providerId={providerId}
          providerType={providerType}
          defaultFilename={activeFile.name}
        />
      )}
    </div>
  );
}